import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { Icon } from '@iconify/react';

const AttackTypeBreakdown = ({ attackTypesByDay, attackTypes }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  const formatDate = (dateStr) => {
    return format(new Date(dateStr), 'MM/dd');
  };
  
  // Define colors consistent with existing charts
  const categoryColors = {
    'Anomaly Score Violation': '#FF9800',
    'Restricted File Access': '#F44336',
    'SQL Injection': '#E91E63',
    'Cross-Site Scripting (XSS)': '#9C27B0',
    'Command Injection': '#673AB7',
    'Path Traversal': '#3F51B5',
    'Protocol Attack': '#2196F3',
    'Request Validation': '#03A9F4',
    'Bot Activity': '#00BCD4',
    'Pattern Matching Attack': '#009688',
    'Other Security Alert': '#607D8B'
  };
  
  // Get all categories that have data
  const activeCategories = attackTypesByDay && attackTypesByDay.length > 0 
    ? Object.keys(attackTypesByDay[0] || {})
      .filter(key => key !== 'day')
      .filter(category => 
        attackTypesByDay.some(day => day[category] > 0)
      )
    : [];
  
  // Handle bar click for drill-down
  const handleBarClick = (data, index) => {
    if (index && index.dataKey) {
      setSelectedCategory(index.dataKey);
      setShowDetails(true);
    }
  };

  // This function is no longer used since we removed legend click functionality

  // Find the selected category data
  const selectedCategoryData = selectedCategory && attackTypes 
    ? attackTypes.find(item => item.type === selectedCategory) 
    : null;

  // Format timestamp to readable date and time
  const formatTimestamp = (timestamp) => {
    return format(new Date(timestamp), 'MM/dd/yyyy');
  };
  
  return (
    <div className="card-container rounded-md p-2 sm:p-3 h-full">
      <div className="flex flex-col md:flex-row gap-2 md:gap-0 justify-between items-start md:items-center mb-2">
        <div className="text-base xl:text-lg font-semibold text-font-primary">Attack Types Over Time</div>
        <div className="flex items-center justify-end flex-wrap w-full md:w-fit">
          {activeCategories.map((category) => (
            <div key={category} className="flex items-center gap-1 text-xs text-font-secondary ml-2 mb-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: categoryColors[category] || '#607D8B' }}></div>
              <span>{category}</span>
            </div>
          ))}
        </div>
      </div>
      
      {!showDetails ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={attackTypesByDay} 
            margin={{ top: 10, right: 30, left: -15, bottom: 0 }}
            onClick={handleBarClick}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
            <XAxis 
              dataKey="day" 
              tickFormatter={formatDate} 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 12 }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 12 }}
            />
            <Tooltip 
              formatter={(value, name) => [value, name]} 
              labelFormatter={formatDate}
              contentStyle={{ backgroundColor: '#F9FAFB', borderRadius: '4px', border: 'none' }}
            />
            {/* Hide the default legend since we have a custom one in the header */}
            <Legend 
              verticalAlign="top"
              height={0}
              content={() => null}
            />
            {activeCategories.map(category => (
              <Bar 
                key={category}
                dataKey={category} 
                stackId="a" 
                fill={categoryColors[category] || '#607D8B'} 
                name={category}
                cursor="pointer"
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="attack-details p-2">
          {selectedCategoryData ? (
            <>
              <div className="mb-4">
                <h4 className="text-lg font-medium text-font-primary">
                  {selectedCategory} ({selectedCategoryData.count} attacks)
                </h4>
              </div>
              
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-bgAccent">
                      <th className="text-left py-2 px-3">Date & Time</th>
                      <th className="text-left py-2 px-3">Attack Pattern</th>
                      <th className="text-left py-2 px-3">Rule ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCategoryData.details && selectedCategoryData.details.length > 0 ? (
                      selectedCategoryData.details.map((detail, index) => (
                        <tr 
                          key={index} 
                          className="border-t border-bgAccent text-font-secondary"
                        >
                          <td className="py-2 px-3">{formatTimestamp(detail.timestamp)}</td>
                          <td className="py-2 px-3 max-w-[400px] break-words">{detail.message}</td>
                          <td className="py-2 px-3 font-mono">{detail.ruleId}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-t border-bgAccent text-font-secondary">
                        <td colSpan={3} className="text-center py-4">
                          No detailed information available for this attack type.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 flex justify-end">
                <button 
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-secondary focus:outline-none"
                  onClick={() => setShowDetails(false)}
                >
                  Back to Overview
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-font-secondary">
              No attack category selected or data unavailable.
              <div className="mt-4">
                <button 
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-secondary focus:outline-none"
                  onClick={() => setShowDetails(false)}
                >
                  Back to Overview
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttackTypeBreakdown;